import React, { useRef, useState } from 'react';
// TODO: Replace with ECharts bar chart implementation
// import {
//   BarChart as RechartsBarChart,
//   Bar,
//   XAxis,
//   YAxis,
//   CartesianGrid,
//   Tooltip,
//   Legend,
//   ResponsiveContainer,
//   TooltipProps,
//   Brush,
//   ReferenceLine
// } from 'recharts';

// Define TooltipProps interface for chart tooltips
interface TooltipProps<TValue, TName> {
  active?: boolean;
  payload?: Array<{
    name?: TName;
    value?: TValue;
    color?: string;
  }>;
  label?: string;
}
import { 
  styled, 
  Box, 
  Typography, 
  useTheme, 
  IconButton,  
  Tooltip as MuiTooltip,
  Menu,
  MenuItem,
  CircularProgress
} from '@mui/material';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import ZoomInIcon from '@mui/icons-material/ZoomIn';
import ZoomOutIcon from '@mui/icons-material/ZoomOut';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { exportChartToPng, exportChartDataToCsv, handleChartExportToPdf } from '../../utils/chartUtils';
import ChartPlaceholder from './ChartPlaceholder';
import { convertBarChartData } from '../../utils/chartDataConverters';

export interface BarChartDataItem {
  name: string;
  [key: string]: string | number;
}

export interface BarSeries {
  dataKey: string;
  name?: string;
  color?: string;
  stackId?: string | number;
  isTarget?: boolean; // To identify target values for reference lines
}

// Add support for chart.js-like data format
export interface ChartJsBarData {
  labels: string[];
  datasets: {
    label: string;
    data: number[];
    backgroundColor: string;
  }[];
}

export interface BarChartProps {
  /** Chart data - can be either BarChartDataItem[] or ChartJsBarData */
  data: BarChartDataItem[] | ChartJsBarData;
  /** Data series to display */
  series?: BarSeries[];
  /** Chart title */
  title?: string;
  /** Chart height */
  height?: number;
  /** X-axis label */
  xAxisLabel?: string;
  /** Y-axis label */
  yAxisLabel?: string;
  /** Show chart grid */
  grid?: boolean;
  /** Stack bars instead of placing side by side */
  stacked?: boolean;
  /** Format tooltips */
  tooltipFormatter?: (value: number | string) => string;
  /** Margin around chart */
  margin?: { top?: number; right?: number; bottom?: number; left?: number };
  /** Enable chart interactions (zoom, pan, export) */
  interactive?: boolean;
  /** Show brush for range selection */
  showBrush?: boolean;
  /** Reference line value (e.g., for thresholds) */
  referenceLine?: { y?: number; x?: string | number; label?: string; color?: string };
  /** Data is refreshing status */
  isRefreshing?: boolean;
  /** Function to refresh data */
  onRefresh?: () => void;
}

const ChartContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  marginBottom: theme.spacing(3),
  position: 'relative',
}));

const ChartHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(1),
}));

const ChartTitle = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightMedium,
}));

const ControlsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}));

const ChartWrapper = styled(Box)(({ theme }) => ({
  width: '100%',
  borderRadius: theme.shape.borderRadius,
  overflow: 'hidden',
  border: `1px solid ${theme.palette.divider}`,
}));

const RefreshIndicator = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  fontSize: '0.75rem',
  color: theme.palette.text.secondary,
  marginRight: theme.spacing(2),
}));

const LoadingOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'rgba(255, 255, 255, 0.7)',
  borderRadius: theme.shape.borderRadius,
  zIndex: 5,
}));

const CustomTooltip: React.FC<TooltipProps<number | string, string>> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          backgroundColor: '#fff',
          border: '1px solid #ccc',
          padding: 1,
          borderRadius: 1,
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Typography variant="body2" fontWeight="bold">
          {label}
        </Typography>
        {payload.map((entry: any, index: number) => (
          <Typography
            key={`tooltip-${index}`}
            variant="body2"
            sx={{ color: entry.color }}
          >
            {`${entry.name}: ${entry.value}`}
          </Typography>
        ))}
      </Box>
    );
  }

  return null;
};

export const BarChart: React.FC<BarChartProps> = ({
  data: inputData,
  series: inputSeries,
  title,
  height = 400,
  xAxisLabel,
  yAxisLabel,
  grid = true,
  stacked = false,
  tooltipFormatter,
  margin = { top: 10, right: 30, left: 0, bottom: 0 },
  interactive = true,
  showBrush = false,
  referenceLine,
  isRefreshing = false,
  onRefresh,
}) => {
  const theme = useTheme();
  const chartRef = useRef<HTMLDivElement>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const exportMenuOpen = Boolean(exportMenuAnchor);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  
  // Handle both data formats (BarChartDataItem[] and ChartJsBarData)
  const data = React.useMemo(() => {
    // Check if data is in Chart.js format
    if (inputData && 
        !Array.isArray(inputData) && 
        'labels' in inputData && 
        'datasets' in inputData) {
      return convertBarChartData(inputData);
    }
    return inputData as BarChartDataItem[];
  }, [inputData]);
  
  // Process series from data if not provided
  const series = React.useMemo(() => {
    if (inputSeries) return inputSeries;
    
    // Extract series from data if not provided
    if (data && data.length > 0) {
      const firstItem = data[0];
      return Object.keys(firstItem)
        .filter(key => key !== 'name')
        .map(key => ({ 
          dataKey: key,
          name: key,
          color: undefined,
          stackId: undefined,
          isTarget: false 
        }));
    }
    return [];
  }, [inputSeries, data]);

  // Filter out target series for reference lines
  const targetSeries = React.useMemo(() => {
    return series.filter(s => s.isTarget);
  }, [series]);

  // Set initial data load timestamp
  React.useEffect(() => {
    if (Array.isArray(data) && data.length > 0 && !lastUpdated) {
      setLastUpdated(new Date());
    }
  }, [data, lastUpdated]);
  
  const chartMargin = {
    top: margin.top || 10,
    right: margin.right || 30,
    bottom: showBrush ? 60 : (margin.bottom || 0),
    left: margin.left || 0,
  };
  
  const handleExportMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };
  
  const handleExportMenuClose = () => {
    setExportMenuAnchor(null);
  };
  
  const handleExportPng = () => {
    handleExportMenuClose();
    exportChartToPng(chartRef, title || 'bar-chart');
  };
  
  const handleExportCsv = () => {
    handleExportMenuClose();
    exportChartDataToCsv(data, title || 'bar-chart-data');
  };
  
  const handleExportPdf = () => {
    handleExportMenuClose();
    handleChartExportToPdf(chartRef, data, title || 'bar-chart', title);
  };
  
  const handleExportPdfWithData = () => {
    handleExportMenuClose();
    handleChartExportToPdf(chartRef, data, title || 'bar-chart', title, true);
  };
  
  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
      setLastUpdated(new Date());
    }
  };

  const formattedLastUpdated = lastUpdated 
    ? `Updated: ${lastUpdated.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit', second:'2-digit'})}`
    : '';
  
  return (
    <ChartContainer>
      <ChartHeader>
        {title && <ChartTitle variant="h6">{title}</ChartTitle>}
        <ControlsContainer>
          {lastUpdated && (
            <RefreshIndicator>
              {formattedLastUpdated}
            </RefreshIndicator>
          )}
          
          {onRefresh && (
            <MuiTooltip title="Refresh data">
              <IconButton size="small" onClick={handleRefresh} disabled={isRefreshing}>
                <RefreshIcon fontSize="small" />
              </IconButton>
            </MuiTooltip>
          )}
          
          {interactive && (
            <>
              <MuiTooltip title="Export options">
                <IconButton size="small" onClick={handleExportMenuOpen}>
                  <DownloadIcon fontSize="small" />
                </IconButton>
              </MuiTooltip>
              <Menu
                anchorEl={exportMenuAnchor}
                open={exportMenuOpen}
                onClose={handleExportMenuClose}
              >
                <MenuItem onClick={handleExportPng}>Export as PNG</MenuItem>
                <MenuItem onClick={handleExportPdf}>Export as PDF</MenuItem>
                <MenuItem onClick={handleExportPdfWithData}>Export as PDF with data table</MenuItem>
                <MenuItem onClick={handleExportCsv}>Export data as CSV</MenuItem>
              </Menu>
            </>
          )}
        </ControlsContainer>
      </ChartHeader>
      
      <ChartWrapper ref={chartRef}>
        {isRefreshing && (
          <LoadingOverlay>
            <CircularProgress size={30} />
          </LoadingOverlay>
        )}

        <ChartPlaceholder
          title={title || "Bar Chart"}
          chartType="Bar Chart"
          height={height}
          width="100%"
        />
      </ChartWrapper>
    </ChartContainer>
  );
};

export default BarChart;
